/**
 * High-performance economic calendar hooks with minimal re-renders
 */

import { useEffect } from "react";
import { useEconomicCalendarStore } from '../stores/useEconomicCalendarStore';

let storageHydrated = false;

function hydrateEconomicCalendarFromStorage() {
  if (storageHydrated || typeof window === "undefined") return;
  storageHydrated = true;
  void useEconomicCalendarStore.getState().loadFromStorage();
}

// Main hook with individual selectors to avoid re-renders
export const useEconomicCalendar = () => {
  useEffect(() => {
    hydrateEconomicCalendarFromStorage();
  }, []);
  const allEvents = useEconomicCalendarStore((state) => state.allEvents);
  const currencies = useEconomicCalendarStore((state) => state.currencies);
  const loadingStatus = useEconomicCalendarStore((state) => state.loadingStatus);
  const dateRange = useEconomicCalendarStore((state) => state.dateRange);
  const isComplete = useEconomicCalendarStore((state) => state.isComplete());
  
  // Get stable function references
  const getEventsByDate = useEconomicCalendarStore((state) => state.getEventsByDate);
  const getEventsByCurrency = useEconomicCalendarStore((state) => state.getEventsByCurrency);
  const getEventsByImpact = useEconomicCalendarStore((state) => state.getEventsByImpact);

  return {
    allEvents,
    currencies,
    loadingStatus,
    dateRange,
    isComplete,
    getEventsByDate,
    getEventsByCurrency,
    getEventsByImpact,
  };
};

// Loading status only (minimal re-renders)
export const useEconomicCalendarLoading = () => {
  const status = useEconomicCalendarStore((state) => state.loadingStatus);
  const errorMessage = useEconomicCalendarStore((state) => state.errorMessage);
  const calendarSyncTargetYears = useEconomicCalendarStore(
    (state) => state.calendarSyncTargetYears
  );
  const loadedCalendarYears = useEconomicCalendarStore(
    (state) => state.loadedCalendarYears
  );

  const targetCount = calendarSyncTargetYears.length;

  return {
    status,
    errorMessage,
    calendarSyncTargetYears,
    loadedCalendarYears,
    progress:
      targetCount === 0
        ? 0
        : Math.round((100 * loadedCalendarYears.length) / targetCount),
  };
};

// Currencies only (for filtering)
export const useEconomicCalendarCurrencies = () => {
  return useEconomicCalendarStore((state) => state.currencies);
};
